import { AlertTriangle, CheckCircle2, Clock, FileSignature, ListChecks, Plus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLE = {
  "OLÖST": "text-red-600",
  "UNDER_UTREDNING": "text-slate-600",
  "GODKÄND": "text-emerald-600",
  "ARKIVERAD": "text-slate-400",
};
const CL_STATUS = {
  "EJ_PÅBÖRJAD": "bg-slate-100 text-slate-700",
  "PÅGÅENDE": "bg-amber-100 text-amber-800",
  "AVVIKELSE": "bg-red-100 text-red-800",
  "SLUTFÖRD": "bg-emerald-100 text-emerald-800",
};

export default function Dashboard({ dash, refresh, onReportDeviation, onOpenChecklists }) {
  const { user, refreshUser } = useAuth();

  const signPolicy = async () => {
    try {
      await api.post("/policy/sign");
      toast.success("Reko-policyn är signerad");
      refreshUser();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!dash) return <div className="text-sm text-slate-500" data-testid="dashboard-loading">Laddar översikt...</div>;

  const riskLabel = dash.risk_analysis.status === "green" ? "Uppdaterad" : dash.risk_analysis.status === "yellow" ? "Förfaller snart" : "Förfallen!";
  const riskColor = dash.risk_analysis.status === "green" ? "text-emerald-600" : dash.risk_analysis.status === "yellow" ? "text-amber-600" : "text-red-600";
  const lastEval = dash.risk_analysis.last_evaluated_at ? new Date(dash.risk_analysis.last_evaluated_at).toLocaleDateString("sv-SE", { year: "numeric", month: "short" }) : "Aldrig";

  return (
    <div className="space-y-6" data-testid="dashboard-view">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-testid="kpi-signing">
          <div className="flex justify-between items-center text-slate-500 text-xs font-medium uppercase tracking-wider">
            <span>Reko-Policy Signering</span><FileSignature className="w-4 h-4 text-blue-500" />
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-3xl font-extrabold text-slate-900">{dash.signing.pct}%</span>
            <span className="text-xs text-amber-600 font-semibold">{dash.signing.signed}/{dash.signing.total} medarbetare</span>
          </div>
          <div className="w-full bg-slate-100 h-2 rounded-full mt-3 overflow-hidden">
            <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${dash.signing.pct}%` }} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-testid="kpi-risk-status">
          <div className="flex justify-between items-center text-slate-500 text-xs font-medium uppercase tracking-wider">
            <span>Riskanalys Status</span><Clock className="w-4 h-4 text-amber-500" />
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-2xl font-extrabold text-slate-900">Senast: {lastEval}</span>
            <span className={`text-xs font-semibold ${riskColor}`}>{riskLabel}</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">Obligatorisk utvärdering minst 1 gång/år.</p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-testid="kpi-deviations">
          <div className="flex justify-between items-center text-slate-500 text-xs font-medium uppercase tracking-wider">
            <span>Rapporterade Avvikelser</span><AlertTriangle className="w-4 h-4 text-red-500" />
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-3xl font-extrabold text-slate-900">{dash.deviations.total}</span>
            {dash.deviations.olosta_generella > 0 && (
              <span className="text-xs text-red-600 font-semibold bg-red-50 px-2 py-0.5 rounded border border-red-200">
                {dash.deviations.olosta_generella} olöst generell
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-2">{dash.deviations.enstaka} enstaka · {dash.deviations.generella} generella</p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-testid="kpi-engagements">
          <div className="flex justify-between items-center text-slate-500 text-xs font-medium uppercase tracking-wider">
            <span>Uppdragskontroller</span><UserCheck className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-3xl font-extrabold text-slate-900">{dash.engagement_pct}%</span>
            <span className="text-xs text-emerald-600 font-semibold">slutförda</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">Andel signerade uppdragschecklistor</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold text-slate-900 flex items-center">
                <ListChecks className="w-4 h-4 text-blue-600 mr-2" />Inkomna Avvikelser & Kvalitetsbrister
              </h2>
              <button
                data-testid="report-deviation-btn" onClick={onReportDeviation}
                className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center shadow-sm"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />Rapportera Avvikelse
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse" data-testid="deviations-table">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                    <th className="p-3">Datum</th><th className="p-3">Kund / Uppdrag</th><th className="p-3">Konsult</th>
                    <th className="p-3">Typ av brist</th><th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {dash.recent_deviations.map((d) => (
                    <tr key={d.id}>
                      <td className="p-3 font-mono text-slate-500">{d.created_at?.slice(0, 10)}</td>
                      <td className="p-3 font-bold text-slate-800">{d.client_name}</td>
                      <td className="p-3">{d.reported_by_name}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded font-bold ${d.type === "Generell/Systemisk" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{d.type}</span>
                      </td>
                      <td className={`p-3 font-bold ${STATUS_STYLE[d.status] || ""}`}>{d.status.charAt(0) + d.status.slice(1).toLowerCase().replace("_", " ")}</td>
                    </tr>
                  ))}
                  {dash.recent_deviations.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-slate-400">Inga avvikelser rapporterade</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center">
              <ListChecks className="w-4 h-4 text-blue-600 mr-2" />Pågående Uppdragschecklistor
            </h2>
            <div className="space-y-3" data-testid="open-checklists">
              {dash.open_checklists.map((c) => (
                <div key={c.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex justify-between items-center">
                  <div>
                    <div className="font-bold text-sm text-slate-800">{c.client_name} — {c.engagement_type}</div>
                    <div className="text-xs text-slate-500">Ansvarig: {c.consultant_name} · Deadline: {c.deadline}</div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${CL_STATUS[c.status]}`}>
                      {c.status === "AVVIKELSE" ? "Avvikelse flaggad" : c.status.charAt(0) + c.status.slice(1).toLowerCase().replace("å", "å")}
                    </span>
                    <button
                      data-testid={`open-checklist-${c.id}`} onClick={onOpenChecklists}
                      className="text-xs bg-white border border-slate-300 hover:bg-slate-100 px-3 py-1 rounded font-medium"
                    >
                      Öppna Checklista
                    </button>
                  </div>
                </div>
              ))}
              {dash.open_checklists.length === 0 && (
                <div className="p-6 text-center text-xs text-slate-400">Alla uppdrag är slutförda</div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-base font-bold text-slate-900 mb-3 flex items-center">
              <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2" />Reko 200–230 Krav
            </h2>
            <ul className="text-xs text-slate-600 space-y-2.5 leading-relaxed">
              <li><strong>Modul 1:</strong> Analysera verksamhet & dokumentera kvalitetsmål.</li>
              <li><strong>Modul 2:</strong> Löpande riskbedömning minst 1 gång/år eller vid ändrad bemanning.</li>
              <li><strong>Modul 3:</strong> Generella & specifika riskreducerande åtgärder på kundnivå.</li>
              <li><strong>Modul 4:</strong> Dokumenterad årlig utvärdering med vem, när & 3 fasta slutsatser.</li>
            </ul>
          </div>

          <div className="bg-blue-900 text-white rounded-xl p-6 shadow-md">
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-300">Obligatoriskt Kvalitetsmål</div>
            <h3 className="text-base font-bold mt-1 mb-2">Efterlevnad av Reko & Yrkesetik</h3>
            <p className="text-xs text-blue-200 mb-4">Auktoriserad redovisningskonsult måste årligen signera efterlevnad av Reko, FAR:s yrkesetiska regler och lagar.</p>
            <div className="p-3 bg-blue-800/60 rounded-lg border border-blue-700/50 mb-4 text-xs" data-testid="policy-sign-status">
              {user.reko_policy_signed ? (
                <>
                  <span className="text-emerald-300 font-bold flex items-center"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Signerad av {user.name}</span>
                  <div className="text-blue-300 text-[10px] mt-0.5 font-mono">
                    Tidsstämpel: {user.reko_signed_at ? new Date(user.reko_signed_at).toLocaleString("sv-SE") : "—"}
                  </div>
                </>
              ) : (
                <span className="text-amber-300 font-bold">Ej signerad ännu</span>
              )}
            </div>
            <button
              data-testid="policy-sign-btn" onClick={signPolicy}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold py-2 px-4 rounded shadow transition-colors"
            >
              <FileSignature className="w-3.5 h-3.5 inline mr-1.5" />
              {user.reko_policy_signed ? "Förnya Digital Signering" : "Signera Digitalt"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
